import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  Trash2,
  ChevronLeft,
  CreditCard,
  Users,
  Calendar,
  Lock,
  Info,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Shield,
  Clock,
  Eye,
  EyeOff,
} from "lucide-react";
import axios from "axios";

const loadScript = (src) => {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const Checkout = () => {
  const [searchParams] = useSearchParams();
  // console.log(searchParams)
  const guestsCount = searchParams.get("guestsCount");
  // console.log(guestsCount)
  const [selectedRooms, setSelectedRooms] = useState([]);
  console.log(selectedRooms);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [guestInfo, setGuestInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    specialRequests: "",
  });
  const [paymentInfo, setPaymentInfo] = useState({
    cardNumber: "",
    cardholderName: "",
    expiryDate: "",
    cvv: "",
    saveCard: false,
  });
  const [errors, setErrors] = useState({});
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [showCardDetails, setShowCardDetails] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const encodedRooms = searchParams.get("rooms");
    const checkIn = searchParams.get("checkIn");
    const checkOut = searchParams.get("checkOut");

    if (!encodedRooms) {
      alert("No room data found. Redirecting...");
      navigate("/room-booking");
      return;
    }

    try {
      const parsedRooms = JSON.parse(decodeURIComponent(encodedRooms));

      // Fetch full room details from backend based on roomId
      const fetchRoomDetails = async () => {
        const fetchedRooms = await Promise.all(
          parsedRooms.map(async (roomInfo) => {
            const res = await axios.get(`https://apigulmohar.dodunsoftsolutions.com/api/rooms/${roomInfo.roomId}`);
            const room = res.data;

            let basePrice = roomInfo.withBreakfast
              ? (roomInfo.priceType === "single"
                ? room.price.singleWithBreakfast
                : room.price.doubleWithBreakfast)
              : (roomInfo.priceType === "single"
                ? room.price.single
                : room.price.double);

            const basePrice2 = basePrice;

            let extraPersonType = "";
            let extraPersonPrice = 0;

            if (roomInfo.extraPersons > 0) {
              extraPersonPrice = roomInfo.withBreakfast
                ? room.price.extraPersonWithBreakfast
                : room.price.extraPerson;

              basePrice += extraPersonPrice * roomInfo.extraPersons;
              extraPersonType = roomInfo.withBreakfast ? "extraPersonWithBreakfast" : "extraPerson";
            }

            // Construct selectedPriceType based on withBreakfast
            const selectedPriceType = roomInfo.withBreakfast
              ? `${roomInfo.priceType}WithBreakfast`
              : roomInfo.priceType;

            return {
              ...room,
              checkIn,
              checkOut,
              price: basePrice,
              basePrice: basePrice2,
              selectedPriceType,
              withBreakfast: roomInfo.withBreakfast,
              extraPersons: roomInfo.extraPersons,
              extraPersonType,
              extraPersonPrice, // Correctly sending this
              totalExtraPersonCost: extraPersonPrice * roomInfo.extraPersons,
            };
          })
        );

        setSelectedRooms(fetchedRooms);
        setLoading(false);
      };

      fetchRoomDetails();
    } catch (err) {
      console.error("Invalid room data in URL:", err);
      navigate("/room-booking");
    }
  }, [searchParams, navigate]);

  // Calculate number of nights
  const calculateNights = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return 0;
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays || 1; // Default to 1 if calculation fails
  };

  // Format price display
  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(price);
  };

  const removeRoom = (roomId) => {
    const updatedRooms = selectedRooms.filter((room) => room._id !== roomId);
    setSelectedRooms(updatedRooms);

    if (updatedRooms.length === 0) {
      navigate("/room-booking");
    }
  };

  // Calculate subtotal price for all rooms
  const calculateSubtotal = () => {
    return selectedRooms.reduce((total, room) => {
      const nights = calculateNights(room.checkIn, room.checkOut);
      return total + room.price * nights;
    }, 0);
  };

  // Apply coupon discount
  const applyCoupon = () => {
    // In a real app, this would validate the coupon with the backend
    if (couponCode.toLowerCase() === "welcome10") {
      const discountAmount = calculateSubtotal() * 0.1; // 10% discount
      setDiscount(discountAmount);
      setAppliedCoupon({
        code: couponCode,
        discountPercentage: 10,
      });
      setSuccessMessage("Coupon applied successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } else {
      setErrors({ ...errors, coupon: "Invalid coupon code" });
      setTimeout(() => setErrors({ ...errors, coupon: "" }), 3000);
    }
    setCouponCode("");
  };

  // Remove applied coupon
  const removeCoupon = () => {
    setAppliedCoupon(null);
    setDiscount(0);
  };

  // Calculate taxes (12%)
  const calculateTaxes = () => {
    return (calculateSubtotal() - discount) * 0.12;
  };

  // Calculate total price including taxes
  const calculateTotal = () => {
    return calculateSubtotal() - discount + calculateTaxes();
  };

  // Format card number with spaces
  const formatCardNumber = (value) => {
    return (
      value
        .replace(/\s/g, "")
        .match(/.{1,4}/g)
        ?.join(" ")
        .substr(0, 19) || ""
    );
  };

  // Format expiry date as MM/YY
  const formatExpiryDate = (value) => {
    return value
      .replace(/\D/g, "")
      .match(/^(\d{0,2})(\d{0,2})/)
      .slice(1)
      .filter(Boolean)
      .join("/");
  };

  // Handle guest info changes
  const handleGuestInfoChange = (e) => {
    const { name, value } = e.target;
    setGuestInfo({
      ...guestInfo,
      [name]: value,
    });

    // Clear error when field is modified
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleCheckout = async () => {
    if (selectedRooms.length === 0) {
      alert("No rooms selected. Please select a room to continue.");
      navigate("/room-booking");
      return;
    }

    if (!validateGuestInfo()) return;

    setBookingLoading(true);
    const totalAmount = calculateTotal();

    try {
      const razorpayRes = await axios.post(
        `https://apigulmohar.dodunsoftsolutions.com/api/bookings/createRazorpayOrder`,
        {
          amount: Math.round(totalAmount * 100),
          currency: "INR",
        }
      );

      const res = await loadScript(
        "https://checkout.razorpay.com/v1/checkout.js"
      );

      if (!res) {
        alert("Razorpay SDK failed to load.");
        setBookingLoading(false);
        return;
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_live_GcHs18pSPnkyAw",
        amount: razorpayRes.data.amount,
        currency: "INR",
        order_id: razorpayRes.data.order_id,
        name: "The Gulmohar Grand ",
        description: "Hotel Room Booking",
        image:
          "https://ik.imagekit.io/tdlebsr5e/TheGulmoharGrand.jpeg?updatedAt=1750152666297",
        handler: async (response) => {
          try {
            const finalBooking = {
              name: `${guestInfo.firstName} ${guestInfo.lastName}`,
              email: guestInfo.email,
              phoneNumber: guestInfo.phone,
              checkIn: selectedRooms[0].checkIn,
              checkOut: selectedRooms[0].checkOut,
              numberOfGuests: parseInt(guestsCount),
              status: "booked",
              paymentStatus: "Prepaid",
              totalAmount: totalAmount,
              amountPaid: totalAmount,
              discount: discount || 0,
              invoiceNumber: `INV-${Date.now()}`,
              specialRequests: guestInfo.specialRequests || "",
              bookingMode: "online",
              rooms: selectedRooms.map((room) => ({
                roomId: room._id,
                roomName: room.roomNumber,
                price: room.price,
                basePrice: room.basePrice,
                basePriceType: room.selectedPriceType,
                extraPersonPrice: room.extraPersonPrice,
                numberOfExtraPersons: room.extraPersons,
                extraPersonType: room.extraPersonType,
                totalExtraPersonCost: room.totalExtraPersonCost,
              })),
              addons: [], // Add this only if using addons
              amountTransactions: [
                {
                  type: "credit",
                  amount: totalAmount,
                  description: "Room booking via Razorpay",
                  paymentMethod: "razorpay",
                },
              ],
            };

            const confirmRes = await axios.post(
              `https://apigulmohar.dodunsoftsolutions.com/api/bookings/confirm-booking`,
              finalBooking
            );

            const emailResponse = await axios.post(
              `https://apigulmohar.dodunsoftsolutions.com/api/bookings/send-mail`,
              finalBooking
            );

            // console.log(emailResponse);

            if (confirmRes.status === 201 && emailResponse.status === 200) {
              const bookingId = confirmRes.data._id;
              alert("Booking confirmed!");

              await handleRoomUpdation();

              localStorage.removeItem("selectedRooms");
              navigate(`/booking/confirmation?id=${bookingId}`);
            }
          } catch (err) {
            console.error("Error confirming booking:", err);
            alert("Failed to confirm booking.");
          } finally {
            setBookingLoading(false);
          }
        },
        prefill: {
          name: `${guestInfo.firstName} ${guestInfo.lastName}`,
          email: guestInfo.email,
          contact: guestInfo.phone,
        },
        notes: {
          address: "Hotel Booking",
        },
        theme: {
          color: "#de7d00",
        },
        modal: {
          ondismiss: function () {
            alert("Payment cancelled.");
          },
        },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (err) {
      console.error("Razorpay error:", err);
      alert("Something went wrong while initiating payment.");
      setBookingLoading(false);
    }
  };

  const handlePostpaidBooking = async () => {
    if (selectedRooms.length === 0) {
      alert("No rooms selected. Please select a room to continue.");
      navigate("/booking");
      return;
    }

    if (!validateGuestInfo()) return;
    setBookingLoading(true);

    const totalAmount = calculateTotal();

    try {
      const finalBooking = {
        name: `${guestInfo.firstName} ${guestInfo.lastName}`,
        email: guestInfo.email,
        phoneNumber: guestInfo.phone,
        checkIn: selectedRooms[0].checkIn,
        checkOut: selectedRooms[0].checkOut,
        numberOfGuests: parseInt(guestsCount),
        status: "booked",
        paymentStatus: "Paid At Hotel",
        totalAmount: totalAmount,
        amountPaid: 0,
        discount: discount || 0,
        invoiceNumber: `INV-${Date.now()}`,
        specialRequests: guestInfo.specialRequests || "",
        bookingMode: "online",
        rooms: selectedRooms.map((room) => ({
          roomId: room._id,
          roomName: room.roomNumber,
          price: room.price,
          basePrice: room.basePrice,
          basePriceType: room.selectedPriceType,
          extraPersonPrice: room.extraPersonPrice,
          numberOfExtraPersons: room.extraPersons,
          extraPersonType: room.extraPersonType,
          totalExtraPersonCost: room.totalExtraPersonCost,
        })),
        addons: [], // Add this only if using addons
        amountTransactions: [
          {
            type: "debit",
            amount: totalAmount,
            description: "Postpaid room booking",
            paymentMethod: "cash",
          },
        ],
      };

      const confirmRes = await axios.post(
        `https://apigulmohar.dodunsoftsolutions.com/api/bookings/confirm-booking`,
        finalBooking
      );

      const emailResponse = await axios.post(
        `https://apigulmohar.dodunsoftsolutions.com/api/bookings/send-mail`,
        finalBooking
      );

      if (confirmRes.status === 201 && emailResponse.status === 200) {
        const bookingId = confirmRes.data._id;
        alert("Booking confirmed!");

        await handleRoomUpdation();

        localStorage.removeItem("selectedRooms");
        navigate(`/booking/confirmation?id=${bookingId}`);
      }
    } catch (err) {
      console.error("Error confirming booking:", err);
      alert("Failed to confirm booking.");
    } finally {
      setBookingLoading(false);
    }
  };

  const handleRoomUpdation = async () => {
    // e.preventDefault();

    if (!selectedRooms || selectedRooms.length === 0) {
      alert("Please select at least one room");
      return;
    }

    try {
      const updatePromises = selectedRooms.map((room) =>
        axios.put(`https://apigulmohar.dodunsoftsolutions.com/api/rooms/${room._id}/status`, {
          status: "booked",
        })
      );

      const results = await Promise.allSettled(updatePromises);

      const failedUpdates = results.filter(
        (result) => result.status === "rejected"
      );

      if (failedUpdates.length === 0) {
        console.log("All rooms booked successfully!");
        // Optionally update UI state or refresh data
      } else {
        alert(
          `${failedUpdates.length} room(s) failed to update. Please try again.`
        );
        console.error("Failed updates:", failedUpdates);
      }
    } catch (error) {
      console.error("Unexpected error during room update:", error);
      alert("An unexpected error occurred while updating the rooms.");
    }
  };

  // Handle payment info changes
  const handlePaymentInfoChange = (e) => {
    const { name, value, checked, type } = e.target;

    if (type === "checkbox") {
      setPaymentInfo({
        ...paymentInfo,
        [name]: checked,
      });
    } else if (name === "cardNumber") {
      setPaymentInfo({
        ...paymentInfo,
        [name]: formatCardNumber(value),
      });
    } else if (name === "expiryDate") {
      setPaymentInfo({
        ...paymentInfo,
        [name]: formatExpiryDate(value),
      });
    } else if (name === "cvv") {
      setPaymentInfo({
        ...paymentInfo,
        [name]: value.replace(/\D/g, "").substring(0, 3),
      });
    } else {
      setPaymentInfo({
        ...paymentInfo,
        [name]: value,
      });
    }

    // Clear error when field is modified
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  // Validate guest info
  const validateGuestInfo = () => {
    const newErrors = {};

    if (!guestInfo.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!guestInfo.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!guestInfo.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(guestInfo.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!guestInfo.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(guestInfo.phone.replace(/\D/g, ""))) {
      newErrors.phone = "Please enter a valid 10-digit phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate payment info
  const validatePaymentInfo = () => {
    const newErrors = {};

    if (!paymentInfo.cardNumber.trim()) {
      newErrors.cardNumber = "Card number is required";
    } else if (paymentInfo.cardNumber.replace(/\s/g, "").length !== 16) {
      newErrors.cardNumber = "Please enter a valid 16-digit card number";
    }

    if (!paymentInfo.cardholderName.trim()) {
      newErrors.cardholderName = "Cardholder name is required";
    }

    if (!paymentInfo.expiryDate) {
      newErrors.expiryDate = "Expiry date is required";
    } else {
      const [month, year] = paymentInfo.expiryDate.split("/");
      const currentYear = new Date().getFullYear() % 100;
      const currentMonth = new Date().getMonth() + 1;

      if (!month || !year || month < 1 || month > 12) {
        newErrors.expiryDate = "Please enter a valid expiry date (MM/YY)";
      } else if (
        year < currentYear ||
        (year == currentYear && month < currentMonth)
      ) {
        newErrors.expiryDate = "Card has expired";
      }
    }

    if (!paymentInfo.cvv) {
      newErrors.cvv = "CVV is required";
    } else if (paymentInfo.cvv.length !== 3) {
      newErrors.cvv = "CVV must be 3 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle next step
  const handleNextStep = () => {
    if (currentStep === 1) {
      if (validateGuestInfo()) {
        setCurrentStep(2);
        window.scrollTo(0, 0);
      }
    }
  };

  // Handle previous step
  const handlePrevStep = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
      window.scrollTo(0, 0);
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (currentStep === 2) {
      if (validatePaymentInfo()) {
        // Process booking - in a real app, send to backend
        alert("Booking confirmed! Thank you for your reservation.");
        // Clear localStorage and redirect to confirmation page
        localStorage.removeItem("selectedRooms");
        navigate("/booking/confirmation");
      }
    }
  };

  // Go back to booking page
  const goBackToBooking = () => {
    navigate("/booking");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-700"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-200">
      {/* Top progress bar */}

      {/* Main content */}
      <div className="container mx-auto px-4 py-36">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left column - Form */}
          <div className="lg:col-span-2">
            {/* Selected rooms panel */}
            <motion.div
              className="bg-white rounded-lg shadow-md p-6 mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-medium">Your Selected Rooms</h2>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                  {selectedRooms.length}{" "}
                  {selectedRooms.length === 1 ? "Room" : "Rooms"}
                </span>
              </div>

              <div className="divide-y divide-gray-100">
                {selectedRooms.map((room) => {
                  const nights = calculateNights(room.checkIn, room.checkOut);

                  return (
                    <div key={room._id} className="py-4 first:pt-0 last:pb-0">
                      <div className="flex gap-4">
                        <div className="w-24 h-20 flex-shrink-0 overflow-hidden rounded-md">
                          <img
                            src={room.images[0]}
                            alt={room.roomType}
                            className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                          />
                        </div>
                        <div className="flex-grow">
                          <div className="flex justify-between">
                            <h3 className="font-medium text-gray-800">
                              {room.roomType}
                            </h3>
                            <button
                              onClick={() => removeRoom(room._id)}
                              className="text-gray-400 hover:text-red-500 transition-colors"
                              aria-label="Remove room"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                          <p className="text-sm text-gray-500">
                            Room {room.roomNumber}, Floor {room.floor}
                          </p>
                          <div className="flex justify-between mt-2">
                            <p className="text-sm text-gray-600 flex items-center">
                              <Calendar className="w-4 h-4 mr-1 text-amber-700" />
                              {new Date(room.checkIn).toLocaleDateString(
                                "en-US",
                                { month: "short", day: "numeric" }
                              )}{" "}
                              -{" "}
                              {new Date(room.checkOut).toLocaleDateString(
                                "en-US",
                                { month: "short", day: "numeric" }
                              )}
                              <span className="mx-1">·</span>
                              {nights} {nights === 1 ? "night" : "nights"}
                            </p>
                            <p className="font-medium text-amber-700">
                              {formatPrice(room.price * nights)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>

            {/* Form steps */}
            <form onSubmit={handleSubmit}>
              <AnimatePresence mode="wait">
                {currentStep === 1 && (
                  <motion.div
                    key="guest-info"
                    className="bg-white rounded-lg shadow-md p-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h2 className="text-xl font-medium mb-1 flex items-center">
                      <Users className="w-5 h-5 mr-2 text-amber-700" />
                      Guest Information
                    </h2>
                    <p className="text-sm text-red-600 mb-5">Kindly ensure your details are filled out correctly.</p>


                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          value={guestInfo.firstName}
                          onChange={handleGuestInfoChange}
                          className={`w-full px-4 py-2 border ${errors.firstName
                            ? "border-red-500"
                            : "border-gray-300"
                            } rounded-md focus:ring-amber-500 focus:border-amber-500 transition-colors`}
                        />
                        {errors.firstName && (
                          <p className="mt-1 text-red-500 text-sm flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {errors.firstName}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          value={guestInfo.lastName}
                          onChange={handleGuestInfoChange}
                          className={`w-full px-4 py-2 border ${errors.lastName
                            ? "border-red-500"
                            : "border-gray-300"
                            } rounded-md focus:ring-amber-500 focus:border-amber-500 transition-colors`}
                        />
                        {errors.lastName && (
                          <p className="mt-1 text-red-500 text-sm flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {errors.lastName}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={guestInfo.email}
                          onChange={handleGuestInfoChange}
                          className={`w-full px-4 py-2 border ${errors.email ? "border-red-500" : "border-gray-300"
                            } rounded-md focus:ring-amber-500 focus:border-amber-500 transition-colors`}
                        />
                        {errors.email && (
                          <p className="mt-1 text-red-500 text-sm flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {errors.email}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={guestInfo.phone}
                          onChange={handleGuestInfoChange}
                          className={`w-full px-4 py-2 border ${errors.phone ? "border-red-500" : "border-gray-300"
                            } rounded-md focus:ring-amber-500 focus:border-amber-500 transition-colors`}
                        />
                        {errors.phone && (
                          <p className="mt-1 text-red-500 text-sm flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {errors.phone}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Special Requests (Optional)
                      </label>
                      <textarea
                        name="specialRequests"
                        value={guestInfo.specialRequests}
                        onChange={handleGuestInfoChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500 h-24"
                        placeholder="Let us know if you have any specific requirements or preferences"
                      ></textarea>
                    </div>

                    <div className="flex justify-end gap-5">
                      <motion.button
                        type="button"
                        onClick={async () => {
                          await handlePostpaidBooking();
                        }}
                        className={`bg-amber-700 hover:bg-amber-600 text-white px-8 py-3 rounded-md font-medium transition-all flex items-center ${bookingLoading ? "opacity-50 cursor-not-allowed" : ""
                          }`} whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {bookingLoading ? (
                          <span className="flex items-center gap-2">
                            <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                            Processing...
                          </span>
                        ) : (
                          "Pay At Hotel"
                        )}
                      </motion.button>
                      <motion.button
                        type="button"
                        onClick={async () => {
                          await handleCheckout();
                        }}
                        className={`bg-amber-700 hover:bg-amber-600 text-white px-8 py-3 rounded-md font-medium transition-all flex items-center ${bookingLoading ? "opacity-50 cursor-not-allowed" : ""
                          }`} whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {bookingLoading ? (
                          <span className="flex items-center gap-2">
                            <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                            Processing...
                          </span>
                        ) : (
                          "Pay Now"
                        )}
                      </motion.button>
                    </div>
                  </motion.div>
                )}

                {/* {currentStep === 2 && (
                  <motion.div 
                    key="payment-info"
                    className="bg-white rounded-lg shadow-md p-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h2 className="text-xl font-medium mb-6 flex items-center">
                      <CreditCard className="w-5 h-5 mr-2 text-amber-700" />
                      Payment Details
                    </h2>
                    
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-md">
                      <p className="text-blue-700 flex items-start">
                        <Info className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                        <span>
                          This is a demo checkout page. No actual payment will be processed. For testing, use any valid-format card details.
                        </span>
                      </p>
                    </div>
                    
                    <div className="mb-6">
                      <div className="bg-gray-50 p-4 rounded-md mb-4 flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="bg-amber-600 rounded-full p-2 mr-3">
                            <Lock className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">Secure Payment</p>
                            <p className="text-sm text-gray-500">Your payment information is encrypted</p>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <div className="w-10 h-6 bg-blue-900 rounded"></div>
                          <div className="w-10 h-6 bg-red-500 rounded flex items-center justify-center">
                            <div className="w-6 h-6 bg-yellow-400 rounded-full opacity-80"></div>
                          </div>
                          <div className="w-10 h-6 bg-green-600 rounded"></div>
                        </div>
                      </div>
                      
                      <div className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Card Number <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <input 
                              type="text" 
                              name="cardNumber"
                              value={paymentInfo.cardNumber}
                              onChange={handlePaymentInfoChange}
                              placeholder="1234 5678 9012 3456"
                              maxLength={19}
                              className={`w-full px-4 py-2 border ${errors.cardNumber ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-amber-500 focus:border-amber-500 transition-colors`}
                            />
                            <CreditCard className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                          </div>
                          {errors.cardNumber && (
                            <p className="mt-1 text-red-500 text-sm flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              {errors.cardNumber}
                            </p>
                          )}
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Cardholder Name <span className="text-red-500">*</span>
                          </label>
                          <input 
                            type="text" 
                            name="cardholderName"
                            value={paymentInfo.cardholderName}
                            onChange={handlePaymentInfoChange}
                            placeholder="Name as it appears on card"
                            className={`w-full px-4 py-2 border ${errors.cardholderName ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-amber-500 focus:border-amber-500 transition-colors`}
                          />
                          {errors.cardholderName && (
                            <p className="mt-1 text-red-500 text-sm flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              {errors.cardholderName}
                            </p>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Expiry Date <span className="text-red-500">*</span>
                            </label>
                            <input 
                              type="text" 
                              name="expiryDate"
                              value={paymentInfo.expiryDate}
                              onChange={handlePaymentInfoChange}
                              placeholder="MM/YY"
                              maxLength={5}
                              className={`w-full px-4 py-2 border ${errors.expiryDate ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-amber-500 focus:border-amber-500 transition-colors`}
                            />
                            {errors.expiryDate && (
                              <p className="mt-1 text-red-500 text-sm flex items-center">
                                <AlertCircle className="w-4 h-4 mr-1" />
                                {errors.expiryDate}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              CVV <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <input 
                                type={showPassword ? "text" : "password"}
                                name="cvv"
                                value={paymentInfo.cvv}
                                onChange={handlePaymentInfoChange}
                                placeholder="123"
                                maxLength={3}
                                className={`w-full px-4 py-2 border ${errors.cvv ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-amber-500 focus:border-amber-500 transition-colors`}
                              />
                              <button 
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                              >
                                {showPassword ? (
                                  <EyeOff className="w-5 h-5" />
                                ) : (
                                  <Eye className="w-5 h-5" />
                                )}
                              </button>
                            </div>
                            {errors.cvv && (
                              <p className="mt-1 text-red-500 text-sm flex items-center">
                                <AlertCircle className="w-4 h-4 mr-1" />
                                {errors.cvv}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="saveCard"
                            name="saveCard"
                            checked={paymentInfo.saveCard}
                            onChange={handlePaymentInfoChange}
                            className="h-4 w-4 text-amber-700 border-gray-300 rounded focus:ring-amber-500"
                          />
                          <label htmlFor="saveCard" className="ml-2 text-sm text-gray-700">
                            Save card for future bookings
                          </label>
                        </div>
                      </div>
                    </div>
                    
                    <div className="border-t border-gray-200 pt-6 flex justify-between">
                      <motion.button 
                        type="button"
                        onClick={handlePrevStep}
                        className="border border-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-50 flex items-center"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <ChevronLeft className="w-4 h-4 mr-2" />
                        Back
                      </motion.button>
                      <motion.button 
                        type="submit"
                        className="bg-amber-700 hover:bg-amber-600 text-white px-8 py-3 rounded-md font-medium transition-all flex items-center"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Complete Booking
                        <Lock className="w-4 h-4 ml-2" />
                      </motion.button>
                    </div>
                  </motion.div>
                )} */}
              </AnimatePresence>
            </form>
          </div>

          {/* Right column - Summary */}
          <div className="lg:col-span-1">
            <motion.div
              className="bg-white rounded-lg shadow-md p-6 sticky top-32"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <h2 className="text-xl font-medium mb-6">Booking Summary</h2>

              <div className="space-y-3 mb-6">
                {selectedRooms.map((room) => {
                  const nights = calculateNights(room.checkIn, room.checkOut);
                  return (
                    <div key={room._id} className="flex justify-between text-sm">
                      <span className="text-gray-600">
                        {room.roomType} ({nights}{" "}
                        {nights === 1 ? "night" : "nights"})
                      </span>
                      <span className="font-medium">
                        {formatPrice(room.price * nights)}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Coupon section */}
              {/* <div className="mb-6">
                <button
                  type="button"
                  onClick={() => setShowCouponForm(!showCouponForm)}
                  className="flex items-center text-amber-700 hover:text-amber-800 transition-colors text-sm font-medium"
                >
                  {showCouponForm ? (
                    <ChevronUp className="w-4 h-4 mr-1" />
                  ) : (
                    <ChevronDown className="w-4 h-4 mr-1" />
                  )}
                  {appliedCoupon ? 'Coupon Applied' : 'Have a coupon code?'}
                </button>
                
                <AnimatePresence>
                  {showCouponForm && !appliedCoupon && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-3 flex">
                        <input
                          type="text"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          placeholder="Enter coupon code"
                          className="w-full px-3 py-2 border border-gray-300 rounded-l-md focus:ring-amber-500 focus:border-amber-500 text-sm"
                        />
                        <button
                          type="button"
                          onClick={applyCoupon}
                          className="bg-amber-700 text-white px-4 py-2 rounded-r-md hover:bg-amber-600 transition-colors text-sm"
                        >
                          Apply
                        </button>
                      </div>
                      {errors.coupon && (
                        <p className="mt-1 text-red-500 text-xs flex items-center">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          {errors.coupon}
                        </p>
                      )}
                      {successMessage && (
                        <p className="mt-1 text-green-500 text-xs flex items-center">
                          <Check className="w-3 h-3 mr-1" />
                          {successMessage}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-2">Try "WELCOME10" for 10% off</p>
                    </motion.div>
                  )}
                  
                  {appliedCoupon && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="mt-3 bg-green-50 border border-green-200 rounded-md p-2 flex justify-between items-center"
                    >
                      <div>
                        <p className="text-sm font-medium text-green-800 flex items-center">
                          <Check className="w-4 h-4 mr-1" />
                          {appliedCoupon.code}
                        </p>
                        <p className="text-xs text-green-600">{appliedCoupon.discountPercentage}% discount applied</p>
                      </div>
                      <button
                        type="button"
                        onClick={removeCoupon}
                        className="text-gray-500 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div> */}

              <div className="border-t border-gray-200 pt-4 mt-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">
                    {formatPrice(calculateSubtotal())}
                  </span>
                </div>

                {discount > 0 && (
                  <div className="flex justify-between text-green-700">
                    <span>Discount</span>
                    <span>-{formatPrice(discount)}</span>
                  </div>
                )}

                <div className="flex justify-between">
                  <span className="text-gray-600">Taxes (12%)</span>
                  <span className="font-medium">
                    {formatPrice(calculateTaxes())}
                  </span>
                </div>

                <div className="flex justify-between font-bold text-lg pt-3 border-t border-gray-200 mt-3">
                  <span>Total</span>
                  <span className="text-amber-700">
                    {formatPrice(calculateTotal())}
                  </span>
                </div>
              </div>

              <div className="mt-6 space-y-3 text-sm text-gray-600">
                <p className="flex items-start">
                  <Clock className="w-4 h-4 mr-2 text-amber-700 flex-shrink-0 mt-0.5" />
                  <span>Check-in: 12:00 PM, Check-out: 12:00 PM</span>
                </p>
                <p className="flex items-start">
                  <Shield className="w-4 h-4 mr-2 text-amber-700 flex-shrink-0 mt-0.5" />
                  <span>Free cancellation up to 24 hours before check-in</span>
                </p>
                <p className="flex items-start">
                  <Lock className="w-4 h-4 mr-2 text-amber-700 flex-shrink-0 mt-0.5" />
                  <span>Secure payment processing</span>
                </p>
              </div>

              {/* Hotel contact info */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="font-medium text-gray-800 mb-2">Need Help?</h3>
                <p className="text-sm text-gray-600 mb-1">
                  Contact our support team:
                </p>
                <p className="text-sm text-amber-700">
                  <a href="tel:+916230562306" className="hover:underline">
                    +91 (0) 6230562306
                  </a>
                </p>
                <p className="text-sm text-amber-700">
                  <a href="mailto:info@gulmohargrandhotel.com" className="hover:underline">
                    info@gulmohargrandhotel.com
                  </a>
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
